/**
 * Empty State Component
 *
 * Displays friendly empty state messages with optional actions
 */

import React from 'react';
import { type LucideIcon, Inbox, Search, AlertCircle, FileText } from 'lucide-react';

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  size?: 'sm' | 'md' | 'lg';
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon = Inbox,
  title,
  description,
  action,
  size = 'md'
}) => {
  const sizes = {
    sm: { icon: 32, title: '1rem', desc: '0.875rem', padding: 'var(--space-lg)' },
    md: { icon: 48, title: '1.25rem', desc: '1rem', padding: 'var(--space-2xl)' },
    lg: { icon: 64, title: '1.5rem', desc: '1.125rem', padding: 'var(--space-3xl)' }
  };

  const sizeConfig = sizes[size];

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: sizeConfig.padding,
      textAlign: 'center',
      color: 'var(--color-gray-500)'
    }}>
      <Icon
        size={sizeConfig.icon}
        style={{
          marginBottom: 'var(--space-lg)',
          color: 'var(--color-gray-400)',
          strokeWidth: 1.5
        }}
      />
      <h3 style={{
        fontSize: sizeConfig.title,
        fontWeight: 600,
        color: 'var(--color-gray-700)',
        marginBottom: 'var(--space-sm)'
      }}>
        {title}
      </h3>
      {description && (
        <p style={{
          fontSize: sizeConfig.desc,
          color: 'var(--color-gray-500)',
          maxWidth: '500px',
          lineHeight: 1.5,
          marginBottom: action ? 'var(--space-xl)' : 0
        }}>
          {description}
        </p>
      )}
      {action && (
        <button
          onClick={action.onClick}
          className="btn btn-primary"
          style={{ marginTop: 'var(--space-md)' }}
        >
          {action.label}
        </button>
      )}
    </div>
  );
};

// Preset empty states for common scenarios
export const NoSearchResults: React.FC<{ onReset?: () => void }> = ({ onReset }) => (
  <EmptyState
    icon={Search}
    title="No Results Found"
    description="Try adjusting your search criteria or filters"
    action={onReset ? { label: 'Clear Filters', onClick: onReset } : undefined}
  />
);

export const NoDataAvailable: React.FC = () => (
  <EmptyState
    icon={Inbox}
    title="No Data Available"
    description="There is no data to display for this carrier"
  />
);

export const NoViolations: React.FC = () => (
  <EmptyState
    icon={FileText}
    title="No Violations Found"
    description="This carrier has a clean inspection record with no violations in the database"
    size="sm"
  />
);

export const NoAlerts: React.FC = () => (
  <EmptyState
    icon={AlertCircle}
    title="No Alerts"
    description="You don't have any alerts at this time"
    size="sm"
  />
);
